/**
 * HUD spotlights (docs/TUTORIAL.md §3.6): when a beat introduces a HUD part, everything else dims for a moment and
 * a pulsing gold ring hugs the part, with a tag naming it ("1ST & 10 = TRY 1 · 10 YARDS TO GO"). The ring stays
 * (without the dim) for the beat.
 *
 * The ring is the part's OWN outline (class `hf-spotlit` on the HUD element: `outline` follows the element's
 * border-radius, transforms and enter animations exactly — backlog item 6: nothing offset, nothing detached; the
 * old separate box copied a rect and drifted off the chyron's slide/scale animations). Only the tag is a separate
 * element, placed from the part's box each frame. Parts that are not on screen yet (the rush ring before the snap)
 * simply light up when they appear.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { TutorialView } from '@hf/client-core/ui-model/types';
import type { HudPart } from '@hf/client-core/ui-model/tutorial/catalog';

/** Where each HUD part lives in the HUD's DOM. */
export const PART_SELECTOR: Record<HudPart, string> = {
  meters: '.hf-hud .hf-meters.home',
  stamina: '.hf-hud .hf-meters.home .hf-stamina',
  power: '.hf-hud .hf-meters.home .hf-power',
  playclock: '.hf-hud .hf-playclock',
  rush: '.hf-hud .hf-rush__ring',
  chyron: '.hf-hud .hf-chyron',
  scorebug: '.hf-hud .hf-scorebug',
  captions: '.hf-hud .hf-captions',
  moments: '.hf-super',
};

/** How long the rest of the screen dims when a spotlight appears (ms). */
const DIM_MS = 2600;

export function Spotlights({ view }: { view: TutorialView }) {
  const spots = view.spotlight ?? [];
  const key = spots.map((s) => s.id).join(',');
  const [dim, setDim] = useState(false);
  useEffect(() => {
    if (spots.length === 0) return;
    // dim only once a spotlit part is actually on screen (the rush ring appears at the snap)
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const wait = (): void => {
      const shown = spots.some((s) => {
        const el = document.querySelector(PART_SELECTOR[s.part]);
        return !!el && el.getBoundingClientRect().width > 2;
      });
      if (!shown) {
        raf = requestAnimationFrame(wait);
        return;
      }
      setDim(true);
      timer = setTimeout(() => setDim(false), DIM_MS);
    };
    wait();
    return () => {
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
      setDim(false);
    };
  }, [key]);
  if (spots.length === 0) return null;
  return (
    <div class={`hf-spots ${dim ? 'dim' : ''}`} aria-hidden="true">
      <div class="hf-spots__scrim" />
      {spots.map((s) => (
        <Ring key={s.part} part={s.part} tag={s.tag} {...(s.mark ? { mark: s.mark } : {})} />
      ))}
    </div>
  );
}

/** Parts whose tag goes BESIDE them (right): the top-centre ring and clock have their own pills right below. */
const TAG_RIGHT: ReadonlySet<HudPart> = new Set<HudPart>(['playclock', 'stamina', 'power', 'meters']);

/**
 * Parts whose tag goes UNDER their whole group: the rush ring's "1 MISSISSIPPI…" pill sits right beside the ring, and
 * a tag beside the ring covered the pill it explains (QA round 3).
 */
const TAG_UNDER_GROUP: Partial<Record<HudPart, string>> = { rush: '.hf-rush' };

/** The bar inside a part that a threshold `mark` is measured on (its fraction of the bar's width). */
const MARK_BAR: Partial<Record<HudPart, string>> = {
  stamina: '.hf-stamina__segs',
  power: '.hf-power__track',
};

function Ring({ part, tag, mark }: { part: HudPart; tag: string; mark?: { at: number; label: string } }) {
  const ref = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);
  const markAt = mark?.at ?? -1;
  useEffect(() => {
    let raf = 0;
    let lit: HTMLElement | null = null;
    const unlight = (): void => {
      lit?.classList.remove('hf-spotlit');
      lit = null;
    };
    const loop = (): void => {
      raf = requestAnimationFrame(loop);
      const el = ref.current;
      if (!el) return;
      const target = document.querySelector(PART_SELECTOR[part]) as HTMLElement | null;
      const r = target?.getBoundingClientRect();
      if (!target || !r || r.width < 2 || r.height < 2) {
        el.classList.remove('show');
        unlight();
        return;
      }
      if (lit !== target) {
        unlight();
        lit = target;
      }
      // re-applied every frame: the HUD may re-render the element's class list
      if (!target.classList.contains('hf-spotlit')) target.classList.add('hf-spotlit');
      el.classList.add('show');
      el.style.left = `${r.left}px`;
      el.style.top = `${r.top}px`;
      el.style.width = `${r.width}px`;
      el.style.height = `${r.height}px`;
      // tag above the part, below it when the part sits at the top of the screen, beside it for the top HUD
      const groupSel = TAG_UNDER_GROUP[part];
      const group = groupSel ? (target.closest(groupSel) as HTMLElement | null) : null;
      el.classList.toggle('below', !group && !TAG_RIGHT.has(part) && r.top < window.innerHeight * 0.3);
      el.classList.toggle('right', !group && TAG_RIGHT.has(part));
      el.classList.toggle('group', !!group);
      if (group) {
        // measured from the group's own box (ring + pill): the tag starts under the ring, below both
        const g = group.getBoundingClientRect();
        el.style.setProperty('--gx', `${(g.left - r.left).toFixed(1)}px`);
        el.style.setProperty('--gy', `${(g.bottom - r.top).toFixed(1)}px`);
      }
      // a threshold drawn ON the bar ("under 15" was a number a 10-segment bar never shows)
      const m = markRef.current;
      if (m && markAt >= 0) {
        const sel = MARK_BAR[part];
        const bar = (sel ? (target.querySelector(sel) as HTMLElement | null) : null) ?? target;
        const b = bar.getBoundingClientRect();
        m.style.left = `${b.left - r.left + b.width * markAt}px`;
        m.style.top = `${b.top - r.top}px`;
        m.style.height = `${b.height}px`;
      }
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      unlight();
    };
  }, [part, markAt]);
  return (
    <div class={`hf-spot part-${part}`} ref={ref}>
      <span class="hf-spot__tag t-broadcast">{tag}</span>
      {mark ? (
        <span class="hf-spot__mark" ref={markRef}>
          <b class="t-broadcast">{mark.label}</b>
        </span>
      ) : null}
    </div>
  );
}
